import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Mail, KeyRound, ArrowRight, Check, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import { API_URL as API } from '@/utils/apiConfig';

export default function SetPasswordPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const getPasswordStrength = (pw) => {
    if (!pw) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: 1, label: t('generated.setpasswordpage_weak_aa21'), color: 'bg-red-500' };
    if (score <= 3) return { level: 2, label: t('generated.setpasswordpage_fair_1cdd'), color: 'bg-yellow-500' };
    if (score <= 4) return { level: 3, label: t('generated.setpasswordpage_good_6cc7'), color: 'bg-blue-500' };
    return { level: 4, label: t('generated.setpasswordpage_strong_bf98'), color: 'bg-green-500' };
  };

  const strength = getPasswordStrength(password);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('generated.setpasswordpage_please_enter_your_71e8'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/google-user/request-password-setup`, { email });
      toast.success(t('generated.setpasswordpage_verification_code_sent_c697'));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error(t('generated.setpasswordpage_please_enter_the_1470'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/google-user/verify-code`, { email, code });
      toast.success(t('generated.setpasswordpage_code_verified_736a'));
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (strength.level < 2) {
      toast.error(t('generated.setpasswordpage_please_use_a_cdcd'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/google-user/set-password`, { email, code, new_password: password });
      toast.success(t('generated.setpasswordpage_password_set_successfully_eb85'));
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-dvh flex flex-col items-center justify-center p-6"
      style={{ background: 'var(--theme-background)' }}
    >
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(24px)',
          border: '1px solid var(--glass-border)',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        }}
      >
        <div className="text-center pt-10 pb-6 px-8">
          <button onClick={() => navigate('/login')} className="inline-block mb-5 hover:scale-105 transition-transform">
            <img src="/logo.png" alt={t('generated.setpasswordpage_eili_001e')} className="h-16 w-16 mx-auto drop-shadow-lg" />
          </button>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>
            {step === 1 && 'Set Up Your Password'}
            {step === 2 && 'Enter Verification Code'}
            {step === 3 && 'Create Your Password'}
          </h1>
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
            {step === 1 && 'Enter your email to receive a verification code'}
            {step === 2 && 'Check your email for the 6-digit code'}
            {step === 3 && 'Choose a strong password for your account'}
          </p>
        </div>

        <div className="px-8 pb-10">
          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
                <Input
                  type="email"
                  placeholder={t('generated.setpasswordpage_your_google_account_d856')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 py-6 rounded-xl"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="set-password-email"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, var(--theme-accent), var(--theme-accentLight))', color: '#fff' }}
                data-testid="set-password-request-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('generated.setpasswordpage_send_verification_code_d629')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
                <Input
                  type="text"
                  placeholder={t('generated.setpasswordpage_6_digit_code_9ba5')}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="pl-12 py-6 rounded-xl text-center text-xl tracking-widest"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="set-password-code"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, var(--theme-accent), var(--theme-accentLight))', color: '#fff' }}
                data-testid="set-password-verify-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('generated.setpasswordpage_verify_code_10b8')}
                <Check className="h-4 w-4" />
              </Button>
              
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-xs text-center py-2"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                <ArrowLeft className="h-3 w-3 inline mr-1" />
                {t('generated.setpasswordpage_use_different_email_464f')}
              </button>
            </form>
          )}

          {/* Step 3: Set Password */}
          {step === 3 && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <PasswordInput
                placeholder={t('generated.setpasswordpage_new_password_a00e')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full py-6 rounded-xl"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="set-password-input"
              />
              
              {password && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-700 overflow-hidden">
                      <div className={`h-full ${strength.color} transition-all duration-300`} style={{ width: `${strength.level * 25}%` }} />
                    </div>
                    <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{strength.label}</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                    {t('generated.setpasswordpage_min_8_characters_7769')}
                  </p>
                </div>
              )}
              
              <Button
                type="submit"
                disabled={loading || strength.level < 2}
                className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, var(--theme-accent), var(--theme-accentLight))', color: '#fff' }}
                data-testid="set-password-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('generated.setpasswordpage_set_password_9aca')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          )}

          <div className="text-center pt-6">
            <button
              onClick={() => navigate('/login')}
              className="text-sm"
              style={{ color: 'var(--theme-textSecondary)' }}
            >
              {t('generated.setpasswordpage_back_to_e781')} <span className="underline" style={{ color: 'var(--theme-accent)' }}>{t('generated.setpasswordpage_login_1cf6')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
